<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Record Immunization</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Step 1: Select Child -->
          <div v-if="step === 1">
            <div class="mb-4">
              <h6 class="mb-3">Select Child to Immunize</h6>

              <!-- Search -->
              <div class="search-box mb-3">
                <div class="position-relative">
                  <input v-model="searchQuery" @input="searchChildren" type="text"
                         class="form-control" placeholder="Search by child name or CBHI number...">
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>

              <!-- Due for Vaccination -->
              <div v-if="dueChildren.length > 0" class="mb-4">
                <h6 class="text-muted mb-2">Due for Vaccination</h6>
                <div class="list-group">
                  <div v-for="child in dueChildren" :key="child.member_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedChild?.member_id === child.member_id}"
                       @click="selectChild(child)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="flex-grow-1">
                        <h6 class="mb-1">{{ child.first_name }} {{ child.surname }}</h6>
                        <p class="mb-0 text-muted">
                          Age: {{ getChildAge(child.date_of_birth) }} •
                          {{ child.cbhi_number || 'No CBHI' }}
                        </p>
                        <small class="text-danger">
                          <i class="bx bx-error-circle"></i>
                          {{ child.overdue_count || child.overdue_vaccines?.length || 0 }} vaccine(s) overdue
                        </small>
                        <!-- Show first few overdue vaccines -->
                        <div v-if="child.vaccines_due_from_schedule && child.vaccines_due_from_schedule.length > 0" class="mt-1">
                          <small class="text-muted d-block">
                            Missing:
                            <span v-for="(vac, idx) in child.vaccines_due_from_schedule.slice(0, 3)" :key="idx">
                              {{ vac.vaccine_name }} (Dose {{ vac.dose_number }})<span v-if="idx < 2 && idx < child.vaccines_due_from_schedule.length - 1">, </span>
                            </span>
                            <span v-if="child.vaccines_due_from_schedule.length > 3">
                              and {{ child.vaccines_due_from_schedule.length - 3 }} more...
                            </span>
                          </small>
                        </div>
                      </div>
                      <div>
                        <span class="badge bg-danger">{{ child.overdue_count || 0 }} Overdue</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0">
                <h6 class="text-muted mb-2">Search Results</h6>
                <div class="list-group">
                  <div v-for="child in searchResults" :key="child.member_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedChild?.member_id === child.member_id}"
                       @click="selectChild(child)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ child.first_name }} {{ child.surname }}</h6>
                        <p class="mb-0 text-muted">
                          Age: {{ getChildAge(child.date_of_birth) }} •
                          {{ child.household_number }}
                        </p>
                      </div>
                      <div>
                        <span v-if="child.vaccination_status === 'up_to_date'"
                              class="badge bg-success">Up to date</span>
                        <span v-else class="badge bg-warning">Check schedule</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div v-else-if="searchQuery && !loading" class="text-center py-4">
                <p class="text-muted">No children found</p>
              </div>

              <!-- No Due Children -->
              <div v-else-if="dueChildren.length === 0 && !searchQuery && !loading" class="text-center py-4">
                <i class="bx bx-check-circle text-success" style="font-size: 48px;"></i>
                <p class="mt-3 text-muted">No children are currently due for vaccination</p>
                <p class="text-muted">Use the search box above to find a specific child</p>
              </div>
            </div>
          </div>

          <!-- Step 2: Vaccination Schedule -->
          <div v-else-if="step === 2">
            <div class="mb-4">
              <h6 class="mb-3">Vaccination Schedule</h6>

              <!-- Child Info -->
              <div class="alert alert-info">
                <div class="d-flex justify-content-between">
                  <div>
                    <h6 class="alert-heading">{{ selectedChild.first_name }} {{ selectedChild.surname }}</h6>
                    <p class="mb-0">
                      Age: {{ getChildAge(selectedChild.date_of_birth) }}<br>
                      CBHI: {{ selectedChild.cbhi_number || 'Not enrolled' }}<br>
                      Household: {{ selectedChild.household_number }}
                    </p>
                  </div>
                  <div class="text-end">
                    <button @click="previousStep" class="btn btn-sm btn-secondary">
                      <i class="bx bx-arrow-back"></i> Change Child
                    </button>
                  </div>
                </div>
              </div>

              <!-- Loading Schedule -->
              <div v-if="loadingSchedule" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading vaccination schedule...</p>
              </div>

              <!-- Due Vaccines -->
              <div v-else-if="dueVaccines.length > 0">
                <h6 class="text-danger mb-3">
                  <i class="bx bx-error-circle"></i> Due Vaccines ({{ dueVaccines.length }})
                </h6>
                <div class="row g-2 mb-4">
                  <div v-for="vaccine in dueVaccines" :key="vaccine.vaccine_name + vaccine.dose_number"
                       class="col-md-6">
                    <div class="form-check card p-3"
                         :class="{'border-primary': selectedVaccines.includes(vaccine.vaccine_name + '_' + vaccine.dose_number)}">
                      <input class="form-check-input" type="checkbox"
                             :id="'vaccine_' + vaccine.vaccine_name + '_' + vaccine.dose_number"
                             :value="vaccine.vaccine_name + '_' + vaccine.dose_number"
                             v-model="selectedVaccines">
                      <label class="form-check-label ms-2"
                             :for="'vaccine_' + vaccine.vaccine_name + '_' + vaccine.dose_number">
                        <strong>{{ vaccine.vaccine_name }}</strong> - Dose {{ vaccine.dose_number }}
                        <br>
                        <small class="text-muted">
                          {{ vaccine.age_display }} • Due: {{ formatDate(vaccine.due_date) }}
                          <span v-if="vaccine.is_overdue" class="text-danger">
                            ({{ vaccine.days_overdue }} days overdue)
                          </span>
                        </small>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Select All Button -->
                <div class="mb-3">
                  <button v-if="selectedVaccines.length < dueVaccines.length"
                          @click="selectAllDueVaccines"
                          class="btn btn-sm btn-outline-primary">
                    <i class="bx bx-check-square"></i> Select All Due Vaccines
                  </button>
                  <button v-else
                          @click="deselectAllVaccines"
                          class="btn btn-sm btn-outline-secondary">
                    <i class="bx bx-square"></i> Deselect All
                  </button>
                </div>
              </div>

              <!-- No Due Vaccines -->
              <div v-else class="text-center py-4">
                <i class="bx bx-check-circle text-success" style="font-size: 48px;"></i>
                <h5 class="mt-3 text-success">All vaccines up to date!</h5>
                <p class="text-muted">This child has received all scheduled vaccines for their age.</p>
              </div>

              <!-- Completed Vaccines -->
              <div v-if="completedVaccines.length > 0" class="mb-4">
                <h6 class="text-success mb-3">
                  <i class="bx bx-check-circle"></i> Completed Vaccines ({{ completedVaccines.length }})
                </h6>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Vaccine</th>
                      <th>Dose</th>
                      <th>Date Given</th>
                      <th>Given By</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="vaccine in completedVaccines" :key="vaccine.vaccine_name + vaccine.dose_number">
                      <td>{{ vaccine.vaccine_name }}</td>
                      <td>{{ vaccine.dose_number }}</td>
                      <td>{{ formatDate(vaccine.given_date) }}</td>
                      <td>{{ vaccine.given_by || '-' }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Vaccine Details -->
          <div v-else-if="step === 3">
            <div class="mb-4">
              <h6 class="mb-3">Vaccine Administration Details</h6>

              <!-- Selected Vaccines Summary -->
              <div class="alert alert-success">
                <h6>Selected Vaccines:</h6>
                <ul class="mb-0">
                  <li v-for="vac in getSelectedVaccineDetails()" :key="vac.id">
                    {{ vac.name }} - Dose {{ vac.dose }}
                  </li>
                </ul>
              </div>

              <!-- Date Given -->
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Date Given <span class="text-danger">*</span></label>
                  <input v-model="vaccineForm.dateGiven" type="date" class="form-control"
                         :max="today" required>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Given At</label>
                  <select v-model="vaccineForm.givenAt" class="form-select">
                    <option value="health_post">Health Post</option>
                    <option value="outreach">Outreach Site</option>
                    <option value="campaign">Campaign</option>
                    <option value="home">Home Visit</option>
                  </select>
                </div>
              </div>

              <!-- Batch Details -->
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Batch Number</label>
                  <input v-model="vaccineForm.batchNumber" type="text" class="form-control"
                         placeholder="e.g., LOT123456">
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Expiry Date</label>
                  <input v-model="vaccineForm.expiryDate" type="date" class="form-control"
                         :min="today">
                </div>
              </div>

              <!-- Manufacturer -->
              <div class="mb-3">
                <label class="form-label">Manufacturer</label>
                <input v-model="vaccineForm.manufacturer" type="text" class="form-control"
                       placeholder="e.g., Serum Institute of India">
              </div>

              <!-- Site and Route -->
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Site Given</label>
                  <select v-model="vaccineForm.siteGiven" class="form-select">
                    <option value="left_arm">Left Arm</option>
                    <option value="right_arm">Right Arm</option>
                    <option value="left_thigh">Left Thigh</option>
                    <option value="right_thigh">Right Thigh</option>
                    <option value="oral">Oral</option>
                  </select>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Route</label>
                  <select v-model="vaccineForm.route" class="form-select">
                    <option value="IM">Intramuscular (IM)</option>
                    <option value="SC">Subcutaneous (SC)</option>
                    <option value="ID">Intradermal (ID)</option>
                    <option value="PO">Oral (PO)</option>
                  </select>
                </div>
              </div>

              <!-- Adverse Reaction -->
              <div class="mb-3">
                <label class="form-label">Adverse Reaction (if any)</label>
                <textarea v-model="vaccineForm.adverseReaction" class="form-control" rows="2"
                          placeholder="Describe any adverse reactions observed..."></textarea>
              </div>

              <!-- Notes -->
              <div class="mb-3">
                <label class="form-label">Additional Notes</label>
                <textarea v-model="vaccineForm.notes" class="form-control" rows="2"
                          placeholder="Any additional notes..."></textarea>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="loadVaccinationSchedule" :disabled="!selectedChild">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <button v-else-if="step === 2" type="button" class="btn btn-primary"
                  @click="nextStep" :disabled="selectedVaccines.length === 0">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <div v-else-if="step === 3">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button type="button" class="btn btn-success" @click="recordImmunization"
                    :disabled="!vaccineForm.dateGiven || submitting">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Recording...
              </span>
              <span v-else>
                <i class="bx bx-check"></i> Record Immunization
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ImmunizationModal",
  props: {
    preselectedMember: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      step: 1,
      loading: false,
      loadingSchedule: false,
      submitting: false,

      // Child selection
      searchQuery: '',
      searchResults: [],
      dueChildren: [],
      selectedChild: null,

      // Vaccination schedule
      dueVaccines: [],
      completedVaccines: [],
      selectedVaccines: [],

      // Vaccine form
      vaccineForm: {
        dateGiven: new Date().toISOString().split('T')[0],
        givenAt: 'health_post',
        batchNumber: '',
        manufacturer: '',
        expiryDate: '',
        siteGiven: 'left_arm',
        route: 'IM',
        adverseReaction: '',
        notes: ''
      },

      today: new Date().toISOString().split('T')[0]
    };
  },

  mounted() {
    // If preselected member is provided, use it
    if (this.preselectedMember) {
      this.selectedChild = this.preselectedMember;
      this.loadVaccinationSchedule();
    } else {
      // Otherwise load due children
      this.loadDueChildren();
    }
  },

  methods: {
    async loadDueChildren() {
      this.loading = true;

      const data = {
        "function": "getChildrenDueForVaccination"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.dueChildren = res.data.data.children || [];
        }
      } catch (e) {
        console.error("Failed to load due children:", e);
      } finally {
        this.loading = false;
      }
    },

    async searchChildren() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }

      this.loading = true;

      const data = {
        "function": "searchMembers",
        "searchTerm": this.searchQuery,
        "searchType": "under5"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.searchResults = res.data.data.members || [];
        }
      } catch (e) {
        console.error("Failed to search children:", e);
      } finally {
        this.loading = false;
      }
    },

    selectChild(child) {
      this.selectedChild = child;
    },

    async loadVaccinationSchedule() {
      if (!this.selectedChild) return;

      this.step = 2;
      this.loadingSchedule = true;

      const data = {
        "function": "getImmunizationSchedule",
        "memberId": this.selectedChild.member_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.dueVaccines = res.data.data.due || [];
          this.completedVaccines = res.data.data.completed || [];
        }
      } catch (e) {
        console.error("Failed to load vaccination schedule:", e);
      } finally {
        this.loadingSchedule = false;
      }
    },

    selectAllDueVaccines() {
      this.selectedVaccines = this.dueVaccines.map(v => v.vaccine_name + '_' + v.dose_number);
    },

    deselectAllVaccines() {
      this.selectedVaccines = [];
    },

    nextStep() {
      if (this.step === 2 && this.selectedVaccines.length > 0) {
        this.step = 3;
      }
    },

    previousStep() {
      if (this.step === 3) {
        this.step = 2;
      } else if (this.step === 2) {
        this.step = 1;
        this.selectedVaccines = [];
      }
    },

    getSelectedVaccineDetails() {
      return this.selectedVaccines.map(v => {
        const [name, dose] = v.split('_');
        return { id: v, name, dose };
      });
    },

    async recordImmunization() {
      if (!this.vaccineForm.dateGiven || this.selectedVaccines.length === 0) return;

      this.submitting = true;

      try {
        // Record each selected vaccine
        for (const vaccine of this.getSelectedVaccineDetails()) {
          const data = {
            "function": "recordImmunization",
            "memberId": this.selectedChild.member_id,
            "vaccineName": vaccine.name,
            "vaccineCode": vaccine.name, // You might want to get the actual code from the vaccine data
            "doseNumber": parseInt(vaccine.dose),
            "dateGiven": this.vaccineForm.dateGiven,
            "batchNumber": this.vaccineForm.batchNumber,
            "manufacturer": this.vaccineForm.manufacturer,
            "expiryDate": this.vaccineForm.expiryDate,
            "siteGiven": this.vaccineForm.siteGiven,
            "route": this.vaccineForm.route,
            "givenAt": this.vaccineForm.givenAt,
            "adverseReaction": this.vaccineForm.adverseReaction
          };

          const res = await executeJson(data, authUrl);

          if (!res.data.success) {
            throw new Error(res.data.message || 'Failed to record immunization');
          }
        }

        this.$emit('recorded', {
          childName: `${this.selectedChild.first_name} ${this.selectedChild.surname}`,
          vaccinesGiven: this.selectedVaccines.length
        });
      } catch (e) {
        console.error("Failed to record immunization:", e);
        alert(e.message || 'Failed to record immunization. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    getChildAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const ageInMonths = (today.getFullYear() - birthDate.getFullYear()) * 12 +
          (today.getMonth() - birthDate.getMonth());

      if (ageInMonths < 12) {
        return `${ageInMonths} months`;
      } else {
        const years = Math.floor(ageInMonths / 12);
        const months = ageInMonths % 12;
        return months > 0 ? `${years} years ${months} months` : `${years} years`;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #556ee6;
  border-color: #556ee6;
}

.list-group-item.active .text-muted {
  color: #c2c9d6 !important;
}

.list-group-item.active .text-danger {
  color: #ffc0c0 !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.form-check.card {
  cursor: pointer;
  transition: all 0.2s;
}

.form-check.card:hover {
  border-color: #556ee6;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.form-check.card.border-primary {
  background-color: rgba(85, 110, 230, 0.05);
  border-width: 2px;
}

.badge {
  font-weight: 500;
}
</style>